import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import { AppDispatch, RootState } from '../../store';
import {
  setPreset,
  setCustomRange,
  setScope,
} from '../../store/slices/pmDashboardSlice';
import { WindowPreset, presetLabel } from '../../utils/dateWindow';

const PRESETS: WindowPreset[] = [
  'TODAY',
  'THIS_WEEK',
  'NEXT_2_WEEKS',
  'NEXT_1_MONTH',
  'NEXT_2_MONTHS',
  'CUSTOM',
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'READY', label: 'Ready' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'WORK_COMPLETE', label: 'Work complete' },
  { value: 'BLOCKED_DEPENDENCY', label: 'Blocked' },
];

const toInputDate = (iso: string) => format(new Date(iso), 'yyyy-MM-dd');

export default function LookAheadFilterBar() {
  const dispatch = useDispatch() as AppDispatch;
  const filters = useSelector((s: RootState) => s.pmDashboard.filters);

  const handlePreset = (preset: WindowPreset) => dispatch(setPreset(preset));

  const handleCustomChange = (key: 'start' | 'end', value: string) => {
    if (!value) return;
    const next = { ...filters.range, [key]: new Date(value).toISOString() };
    dispatch(setCustomRange(next));
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="overline" color="text.secondary">
            Look-ahead window
          </Typography>
          <ButtonGroup size="small" sx={{ flexWrap: 'wrap', mt: 0.5 }}>
            {PRESETS.map((preset) => (
              <Button
                key={preset}
                variant={filters.preset === preset ? 'contained' : 'outlined'}
                onClick={() => handlePreset(preset)}
              >
                {presetLabel[preset]}
              </Button>
            ))}
          </ButtonGroup>
        </Box>

        {filters.preset === 'CUSTOM' && (
          <Stack direction="row" spacing={2}>
            <TextField
              label="From"
              type="date"
              size="small"
              value={toInputDate(filters.range.start)}
              onChange={(e) => handleCustomChange('start', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              value={toInputDate(filters.range.end)}
              onChange={(e) => handleCustomChange('end', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        )}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={filters.status || ''}
              onChange={(e) =>
                dispatch(setScope({ status: e.target.value || undefined }))
              }
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={!!filters.criticalOnly}
                onChange={(e) =>
                  dispatch(setScope({ criticalOnly: e.target.checked }))
                }
              />
            }
            label="Critical-path only"
          />
        </Stack>
      </Stack>
    </Paper>
  );
}
